import React, { FC } from 'react';
import styled from 'styled-components';
import {
    SkeletonBlock,
    SkeletonFooterBar,
    SkeletonPageHeader,
    SkeletonPageMenu,
    SkeletonPageMenuContentContainer,
    SkeletonPageMenuLayoutContainer,
    SkeletonPageShell,
    SkeletonRow,
    SkeletonStack
} from 'src/components/widgets/skeleton';

const PrintTypeSkeletonShell = styled(SkeletonPageShell)`
    padding-bottom: 92px;
`;

const PrintTypeSkeletonSection = styled.section`
    display: flex;
    flex-direction: column;
    overflow: hidden;
    min-width: 0;
    border: 1px solid #eef1f3;
    border-radius: 16px;
    background: #ffffff;
`;

const PrintTypeSkeletonSectionHeader = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 24px 24px 0;
`;

const PrintTypeSkeletonSectionContent = styled.div`
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 18px;
    padding: 24px;
    min-width: 0;

    @media (max-width: 760px) {
        grid-template-columns: minmax(0, 1fr);
    }
`;

const PrintTypeSkeletonFieldGroup = styled.div<{ $wide?: boolean }>`
    display: flex;
    flex-direction: column;
    gap: 8px;
    min-width: 0;
    grid-column: ${(props) => (props.$wide ? '1 / -1' : 'auto')};
`;

const PrintTypeSkeletonOptions = styled.div`
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 12px;
    grid-column: 1 / -1;

    @media (max-width: 1020px) {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    @media (max-width: 600px) {
        grid-template-columns: minmax(0, 1fr);
    }
`;

const PrintTypeSkeletonFooter = styled(SkeletonFooterBar).attrs({ $padding: '18px 48px' })``;

const menuLineWidths = ['62px', '118px', '58px', '42px', '94px', '96px', '118px', '54px', '108px'];
const sectionTitleWidths = ['88px', '132px', '72px', '48px'];

const FieldSkeleton: FC<{ labelWidth?: string; wide?: boolean; height?: string }> = ({ labelWidth = '92px', wide = false, height = '40px' }) => (
    <PrintTypeSkeletonFieldGroup $wide={wide}>
        <SkeletonBlock $width={labelWidth} $height='12px' />
        <SkeletonBlock $height={height} $radius='6px' />
    </PrintTypeSkeletonFieldGroup>
);

export const PrintTypePageSkeleton: FC = () => (
    <PrintTypeSkeletonShell aria-hidden='true'>
        <SkeletonPageHeader>
            <SkeletonStack $gap='8px' $flex='1'>
                <SkeletonBlock $width='min(240px, 100%)' $height='26px' />
                <SkeletonBlock $width='min(360px, 100%)' $height='13px' />
            </SkeletonStack>
        </SkeletonPageHeader>
        <SkeletonPageMenuLayoutContainer>
            <SkeletonPageMenu itemWidths={menuLineWidths} subItemIndexes={[4, 5]} />
            <SkeletonPageMenuContentContainer>
                <SkeletonStack $gap='20px'>
                    {sectionTitleWidths.map((titleWidth, index) => (
                        <PrintTypeSkeletonSection key={`${titleWidth}-${index}`}>
                            <PrintTypeSkeletonSectionHeader>
                                <SkeletonBlock $width={titleWidth} $height='18px' />
                                {index !== 0 && <SkeletonBlock $width='min(520px, 100%)' $height='13px' />}
                            </PrintTypeSkeletonSectionHeader>
                            <PrintTypeSkeletonSectionContent>
                                {index === 0 && (
                                    <>
                                        <FieldSkeleton labelWidth='44px' wide />
                                        <SkeletonRow $gap='10px'>
                                            <SkeletonBlock $width='18px' $height='18px' $radius='4px' />
                                            <SkeletonBlock $width='124px' $height='14px' />
                                        </SkeletonRow>
                                    </>
                                )}
                                {index === 1 && (
                                    <>
                                        <PrintTypeSkeletonOptions>
                                            {Array.from({ length: 4 }).map((_, optionIndex) => (
                                                <SkeletonRow key={optionIndex} $gap='10px'>
                                                    <SkeletonBlock $width='18px' $height='18px' $radius='4px' />
                                                    <SkeletonBlock $width={optionIndex === 0 ? '34px' : '42px'} $height='14px' />
                                                </SkeletonRow>
                                            ))}
                                        </PrintTypeSkeletonOptions>
                                        <FieldSkeleton labelWidth='32px' />
                                        <FieldSkeleton labelWidth='96px' />
                                        <FieldSkeleton labelWidth='124px' wide />
                                    </>
                                )}
                                {index === 2 && (
                                    <>
                                        <SkeletonRow $gap='10px'>
                                            <SkeletonBlock $width='18px' $height='18px' $radius='4px' />
                                            <SkeletonBlock $width='128px' $height='14px' />
                                        </SkeletonRow>
                                        <SkeletonRow $gap='10px'>
                                            <SkeletonBlock $width='18px' $height='18px' $radius='4px' />
                                            <SkeletonBlock $width='152px' $height='14px' />
                                        </SkeletonRow>
                                        <FieldSkeleton labelWidth='120px' wide />
                                    </>
                                )}
                                {index === 3 && (
                                    <>
                                        <FieldSkeleton labelWidth='112px' />
                                        <FieldSkeleton labelWidth='86px' />
                                        <FieldSkeleton labelWidth='130px' wide height='76px' />
                                    </>
                                )}
                            </PrintTypeSkeletonSectionContent>
                        </PrintTypeSkeletonSection>
                    ))}
                </SkeletonStack>
            </SkeletonPageMenuContentContainer>
        </SkeletonPageMenuLayoutContainer>
        <PrintTypeSkeletonFooter>
            <SkeletonBlock $width='150px' $height='42px' $radius='6px' />
        </PrintTypeSkeletonFooter>
    </PrintTypeSkeletonShell>
);
